import {
  MEAL_METRICS,
  type MealCondition,
  type MealMetric,
} from "@/lib/schema";

/**
 * Betingelsesrekkefølge (CONTEXT.md, docs/adr/0021): the one order Conditions
 * render in, wherever they appear. Derived, never authored — there is no
 * reordering gesture anywhere.
 *
 * The keys, in order:
 *
 * 1. The Metric's weight, largest first — the points its Conditions can
 *    contribute to a day. Tiers stack in `scoreDay`, so that is their sum.
 * 2. Catalog order, breaking a weight tie with something fixed rather than a
 *    coin flip. Keys 1 and 2 are both functions of the Metric alone, which is
 *    what keeps a Metric's Conditions contiguous.
 * 3. Minst before Høyst — inside one Metric, a floor then a ceiling reads as
 *    a range.
 * 4. Lenient before strict, which runs opposite ways for the two directions:
 *    a lower floor is easier to meet, a higher ceiling is.
 *
 * A full tie is left to the stable sort, so the stored array order survives
 * as the last word between two otherwise identical Conditions.
 */
export function orderedConditions(
  conditions: MealCondition[],
): MealCondition[] {
  const weights = metricWeights(conditions);
  return [...conditions].sort((a, b) =>
    compareKeys(sortKey(a, weights), sortKey(b, weights)),
  );
}

function metricWeights(conditions: MealCondition[]): Map<MealMetric, number> {
  const weights = new Map<MealMetric, number>();
  for (const condition of conditions) {
    const accumulated = weights.get(condition.metric) ?? 0;
    weights.set(condition.metric, accumulated + condition.points);
  }
  return weights;
}

function sortKey(
  condition: MealCondition,
  weights: Map<MealMetric, number>,
): number[] {
  const lenient =
    condition.direction === "atLeast"
      ? condition.threshold
      : -condition.threshold;
  return [
    -(weights.get(condition.metric) ?? 0),
    MEAL_METRICS.indexOf(condition.metric),
    condition.direction === "atLeast" ? 0 : 1,
    lenient,
  ];
}

function compareKeys(a: number[], b: number[]): number {
  for (let i = 0; i < a.length; i++) {
    const difference = a[i] - b[i];
    // 0 falls through to the next key, and so does NaN — which is what an
    // unsaved Condition's blank threshold produces. A comparator that
    // returned NaN would leave the whole order up to the sort algorithm.
    if (difference) return difference;
  }
  return 0;
}
